"use client";

import {
  Box,
  Button,
  Field,
  HStack,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";
import type {
  AssessmentFeedbackReason,
  CreateAssessmentFeedbackRequest,
  ThoughtDimensionAttributes,
} from "@/generated/fastapi";
import { feedbackReasonsDemoV1, instrumentDemoV1 } from "@/generated/assessment";
import {
  getDefaultAssessmentInstrumentId,
  getLikert5pAgreementOptions,
} from "@/shared/config/backend";
import { showErrorToast, showSuccessToast } from "@/shared/feedback";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericListbox from "@/shared/ui/controls/GenericListbox";
import WithGenericDialog from "@/shared/ui/gestures/WithGenericDialog";
import useCreateAssessmentFeedbackMutation from "../mutations/useCreateAssessmentFeedbackMutation";

type FeedbackReason =
  (typeof feedbackReasonsDemoV1.feedback_reasons)[number]["value"] &
    AssessmentFeedbackReason;

type ReflectionFeedbackActionProps = {
  thoughtId: string;
  thoughtWording: string;
  dimensionKey: ThoughtDimensionAttributes;
  questionSnapshot: string;
};

const FEEDBACK_REASONS = feedbackReasonsDemoV1.feedback_reasons as readonly {
  value: FeedbackReason;
  label: string;
}[];

function buildAnswerOptionsSnapshot() {
  return getLikert5pAgreementOptions().map((option) => ({
    value: option.value,
    label: option.label,
  }));
}

export default function ReflectionFeedbackAction({
  thoughtId,
  thoughtWording,
  dimensionKey,
  questionSnapshot,
}: ReflectionFeedbackActionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<FeedbackReason[]>([]);
  const [note, setNote] = useState("");
  const createFeedbackMutation = useCreateAssessmentFeedbackMutation();
  const answerOptionsSnapshot = useMemo(() => buildAnswerOptionsSnapshot(), []);
  const hasSelectedReasons = selectedReasons.length > 0;
  const isSaving = createFeedbackMutation.isPending;

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!hasSelectedReasons) {
      return;
    }

    const payload: CreateAssessmentFeedbackRequest = {
      target: "thought_question_mismatch",
      thought_id: thoughtId,
      dimension_key: dimensionKey,
      feedback_reasons: selectedReasons,
      feedback_note: note.trim() || null,
      question_snapshot: questionSnapshot,
      answer_options_snapshot: answerOptionsSnapshot,
      instrument_id: getDefaultAssessmentInstrumentId(),
      instrument_version: instrumentDemoV1.resource_version,
    };

    try {
      await createFeedbackMutation.mutateAsync(payload);
      showSuccessToast("Feedback saved");
      setNote("");
      setSelectedReasons([]);
      setIsOpen(false);
    } catch {
      showErrorToast("Could not save feedback");
    }
  }, [
    answerOptionsSnapshot,
    createFeedbackMutation,
    dimensionKey,
    hasSelectedReasons,
    note,
    questionSnapshot,
    selectedReasons,
    thoughtId,
  ]);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="xs"
        minW="8"
        aria-label="Question does not fit"
        title="Question does not fit"
        onClick={() => setIsOpen(true)}
      >
        <GenericIcon
          icon="collect_feedback"
          size="xs"
          humanReadable="Question does not fit"
        />
      </Button>

      <WithGenericDialog
        title="Question does not fit"
        isOpen={isOpen}
        onOpenChangeAction={setIsOpen}
        onCloseAction={handleClose}
      >
        <Stack gap="4" align="stretch">
          <Stack gap="2" align="stretch">
            <Box>
              <Text fontSize="xs" color="moveon.text.muted">
                Thought
              </Text>
              <Text fontSize="sm" lineClamp={3}>
                {thoughtWording}
              </Text>
            </Box>

            <Box>
              <Text fontSize="xs" color="moveon.text.muted">
                Reflection prompt
              </Text>
              <Text fontSize="sm" lineClamp={3}>
                {questionSnapshot}
              </Text>
            </Box>
          </Stack>

          <Field.Root>
            <GenericListbox<FeedbackReason>
              previewItems={FEEDBACK_REASONS}
              stateObject={selectedReasons}
              stateObjectSetter={setSelectedReasons}
              allowMultiple
              labelText="Reason(s)"
              width="full"
            />
            {!hasSelectedReasons && (
              <Field.HelperText>
                Select at least one reason before saving.
              </Field.HelperText>
            )}
          </Field.Root>

          <Field.Root>
            <Field.Label>What would make this easier to answer?</Field.Label>
            <Textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Optional note"
              rows={4}
            />
          </Field.Root>

          <HStack justify="end" gap="2">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="button"
              bg="moveon.primary"
              color="white"
              disabled={!hasSelectedReasons || isSaving}
              loading={isSaving}
              onClick={handleSubmit}
            >
              Save feedback
            </Button>
          </HStack>
        </Stack>
      </WithGenericDialog>
    </>
  );
}
